// Puerto verbatim de packages/hce/src/hce/offline/outbox.ts.
import type { MedplumClient } from '@medplum/core';
import type { Resource } from '@medplum/fhirtypes';
import { idbDelete, idbGetAll, idbPut, STORE_OUTBOX } from './db';

export const TAG_SYSTEM = 'http://logiacare.ec/fhir/tags';
export const TAG_CREADO_OFFLINE = 'creado-offline';
export const TAG_AUDITADO_DIFERIDO = 'auditado-diferido';

export interface EntradaOutbox {
  localId: string;
  resourceType: string;
  cuerpo: Resource;
  creadoEn: string;
}

type ListenerCambios = (pendientes: number) => void;
const listeners = new Set<ListenerCambios>();

async function notificar(): Promise<void> {
  const total = (await idbGetAll<EntradaOutbox>(STORE_OUTBOX)).length;
  listeners.forEach((l) => l(total));
}

export function onCambioOutbox(listener: ListenerCambios): () => void {
  listeners.add(listener);
  idbGetAll<EntradaOutbox>(STORE_OUTBOX).then((items) => listener(items.length));
  return () => listeners.delete(listener);
}

export async function encolarEscritura(resourceType: string, cuerpo: Resource): Promise<Resource> {
  const localId = `offline-${crypto.randomUUID()}`;
  const ahora = new Date().toISOString();
  const recursoLocal: Resource = {
    ...cuerpo,
    id: localId,
    meta: { ...cuerpo.meta, tag: [...(cuerpo.meta?.tag ?? []), { system: TAG_SYSTEM, code: TAG_CREADO_OFFLINE, display: 'Creado sin conexión — pendiente de sincronizar' }] },
  };
  await idbPut<EntradaOutbox>(STORE_OUTBOX, { localId, resourceType, cuerpo: recursoLocal, creadoEn: ahora });
  await notificar();
  return recursoLocal;
}

export async function contarPendientes(): Promise<number> {
  return (await idbGetAll<EntradaOutbox>(STORE_OUTBOX)).length;
}

export async function vaciarOutbox(
  medplum: MedplumClient,
  reportarDiferido: (resourceType: string, targetRef: string, creadoEn: string) => Promise<void>
): Promise<{ sincronizados: number; fallidos: number }> {
  const pendientes = await idbGetAll<EntradaOutbox>(STORE_OUTBOX);
  let sincronizados = 0;
  let fallidos = 0;
  for (const entrada of pendientes) {
    try {
      const { id: _localId, ...sinIdLocal } = entrada.cuerpo;
      const tagsSinCreadoOffline = (sinIdLocal.meta?.tag ?? []).filter((t) => t.code !== TAG_CREADO_OFFLINE);
      // eslint-disable-next-line no-await-in-loop -- deben sincronizarse en orden, no en paralelo (pueden referenciarse entre sí).
      const creado = await medplum.createResource({
        ...sinIdLocal,
        meta: { ...sinIdLocal.meta, tag: [...tagsSinCreadoOffline, { system: TAG_SYSTEM, code: TAG_AUDITADO_DIFERIDO, display: 'Creado sin conexión, auditado al reconectar' }] },
      });
      // eslint-disable-next-line no-await-in-loop
      await reportarDiferido(entrada.resourceType, `${entrada.resourceType}/${creado.id}`, entrada.creadoEn);
      // eslint-disable-next-line no-await-in-loop
      await idbDelete(STORE_OUTBOX, entrada.localId);
      sincronizados++;
    } catch {
      fallidos++;
    }
  }
  await notificar();
  return { sincronizados, fallidos };
}
